/**
 * Catálogo do domínio `automacao` (T53, CAT-02).
 *
 * Origem: `src/shared/autorizacao/Automacao.ts`.
 *
 * **Duas coisas do arquivo de origem que este catálogo não reproduz, e não pode.**
 *
 * 1. `Automacao.ts:8-14` faz `return build()` dentro do ramo de `admin_automacao`,
 *    o que **descarta** as regras dos ramos seguintes. Catálogo é aditivo por
 *    definição, e a D7 do AD-031 decidiu em favor do catálogo.
 *
 *    A T53 escreveu aqui que "o admin não passa a ter nada que o `manage` não
 *    desse". **É falso por uma célula, e o Verifier do batch A3 mediu:** quem tem
 *    `admin_automacao` **e** `automacao` ganha `create@EapOS`. `EapOS` não está
 *    entre os cinco recursos de `automacao.admin`, então o `manage` não o alcança
 *    — quem o concede é `automacao.escrita`, no ramo que o `return` cortava. O
 *    caso `automacao.spec.ts` que guarda esta frase compara as duas roles juntas,
 *    e não o admin sozinho, que era o furo.
 * 2. ~~`Automacao.ts:35` concede `read` e `create` em `OS_AUTOMACAO` a
 *    `site_manager`, e não existe papel que carregue essa concessão.~~
 *    **Resolvido na T128 (AD-031 D3):** nasceu `automacao.criacao`, e
 *    `GRUPOS.SITE_MANAGER` o carrega. A concessão continua fora de
 *    `automacao.escrita` pelo motivo original — somá-la lá a daria a quem só tem
 *    `automacao`. **O `read` veio junto**, ampla e sem escopo (`acoes:
 *    ["create", "read"]` em `automacao.criacao`) — decisão do usuário em AD-034
 *    D1, textual
 *    "manter amplo". O motivo está na nota do papel. Este cabeçalho dizia o
 *    contrário desde `01472581` e foi corrigido em 2026-09-06: o cabeçalho é o
 *    primeiro texto que alguém lê, e ele estava desmentindo o código.
 */
import type { NomePapel, Papel } from "../catalogo/vocabulario";

export const PAPEIS_AUTOMACAO = {
  "automacao.admin": {
    dominio: "automacao",
    nivel: "admin",
    concessoes: [
      { acoes: ["manage"], recurso: "AUTOMACAO" },
      { acoes: ["manage"], recurso: "DIARIO_OS" },
      { acoes: ["manage"], recurso: "OS_AUTOMACAO" },
      { acoes: ["manage"], recurso: "CONTRATO_AUTOMACAO" },
      { acoes: ["manage"], recurso: "PEDIDO_AUTOMACAO" },
    ],
  },
  "automacao.config": {
    dominio: "automacao",
    nivel: "config",
    concessoes: [
      { acoes: ["config"], recurso: "AUTOMACAO" },
      { acoes: ["read", "create", "update", "delete"], recurso: "CONTRATO_AUTOMACAO" },
      { acoes: ["read", "aprovar", "finalizar"], recurso: "PEDIDO_AUTOMACAO" },
    ],
  },
  "automacao.escrita": {
    dominio: "automacao",
    nivel: "escrita",
    concessoes: [
      { acoes: ["read", "create"], recurso: "AUTOMACAO" },
      { acoes: ["create"], recurso: "DIARIO_OS", condicao: { area: "AUTOMACAO" } },
      { acoes: ["create"], recurso: "EapOS", condicao: { area: "AUTOMACAO" } },
      { acoes: ["read"], recurso: "CONTRATO_AUTOMACAO" },
      { acoes: ["read", "create"], recurso: "PEDIDO_AUTOMACAO" },
    ],
  },
  /**
   * Criar OS de automação (T128, AD-031 D3).
   *
   * `OS_AUTOMACAO` é **gate de módulo puro**: não existe classe com `modelName`
   * igual a ele. É a string que `ListarOsTicket.vue:8` usa para decidir se o botão
   * "NOVA OS AUTOMAÇÃO" aparece.
   *
   * **`create` e `read`, a leitura AMPLA — decisão do usuário em 2026-09-05
   * (AD-034), textual: "manter amplo".**
   *
   * O pedido original era *"criar OS de qualquer matéria e ler as OS relacionadas
   * a sua obra"*. A metade "da sua obra" não cabe no modelo: `EapOS` guarda
   * `obra: string` (id) e nenhum campo de pessoa além de `criado_por`
   * (`shared/eap/classes/OrdemServico.ts:55,70`), então casar esse id contra a lista de
   * obras é **escopo de usuário**, e o `PADRONIZACAO-ROLES.md §8.1` reserva escopo
   * a `tipo: "cliente"`. Das três saídas, o usuário escolheu a terceira: leitura
   * ampla, como no RDO. É coerente com a D6 da mesma rodada, que decidiu que o
   * site manager lê RDO de **todas** as obras.
   *
   * Consequência aceita: quem tem o papel lê OS de todas as obras.
   *
   * O par deste papel é `field.criacao`. São dois e não um porque cada domínio
   * declara o seu recurso — é o que faz dezoito PRs de domínio tocarem dezoito
   * arquivos (`ADR-0003`). O "perfil único" que a decisão pede é o **grupo**, que
   * é exatamente para isso que grupo existe.
   */
  "automacao.criacao": {
    dominio: "automacao",
    nivel: "criacao",
    concessoes: [{ acoes: ["create", "read"], recurso: "OS_AUTOMACAO" }],
  },
  /**
   * Sem chave em `LEGADO` e sem regra própria no arquivo de origem: existe porque
   * `GRUPOS.MKT` cita `automacao.leitura`. Concede o mesmo `read` em `AUTOMACAO`
   * que a escrita já dá.
   */
  "automacao.leitura": {
    dominio: "automacao",
    nivel: "leitura",
    concessoes: [{ acoes: ["read"], recurso: "AUTOMACAO" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;
